import React, { useEffect, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import EditIcon from "@mui/icons-material/Edit";
import { isBlank, notBlank } from "../util/ext";
import { Link, useResolvedPath, useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import { find, map, not, propEq } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useOrganizations } from "../user-admin/userServices";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
  },

  contentCell: {
    width: "33%",
  },

  cell: {
    minWidth: 135,
  },
}));

const ProgramTable = ({ programs, onScrollVisible }) => {
  const url = useResolvedPath(".").pathname;
  const navigate = useNavigate();
  const classes = useStyles();

  const [libraries, setLibraries] = useState([]);
  const libraryResponse = useOrganizations();
  useEffect(() => {
    if (libraryResponse.isSuccess) {
      setLibraries(libraryResponse.data);
    }
  }, [libraryResponse.isSuccess, libraryResponse.data]);

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible, tableContainerEl]);

  const findOrganization = organizationId => {
    const organization = find(
      propEq("organizationId", organizationId),
      libraries,
    );

    return (
      <Typography color={not(organization.active) ? "error" : "inherit"}>
        {organization.organizationName}
      </Typography>
    );
  };

  const handleRowClick = programId => event => {
    navigate(`${url}/${programId}`, {
      state: {
        label: "Back to All Progams",
        previousLocation: "/manage-csp/programs",
      },
    });
  };

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer
        ref={tableContainerEl}
        style={{ height: "calc(100vh - 350px)" }}
      >
        <Table stickyHeader aria-label="Programs">
          <TableHead>
            <TableRow>
              <TableCell
                key="program"
                align="left"
                className={classes.contentCell}
              >
                Name
              </TableCell>

              <TableCell
                key="program_key"
                align="left"
                className={classes.cell}
              >
                Program Key
              </TableCell>

              <TableCell
                key="organization"
                align="left"
                className={classes.cell}
              >
                Organization
              </TableCell>

              <TableCell
                key="actions-edit"
                align="center"
                style={{ width: 50 }}
              >
                Edit
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {map(
              program => (
                <TableRow
                  onClick={handleRowClick(program.programId)}
                  style={{ cursor: "pointer" }}
                  hover
                  tabIndex={-1}
                  key={program.programId}
                >
                  <TableCell
                    key={"name:" + program.programId}
                    align="left"
                    className={classes.contentCell}
                  >
                    {program.programName}
                  </TableCell>

                  <TableCell
                    key={"key:" + program.programId}
                    align="left"
                    className={classes.contentCell}
                  >
                    {program.programKey}
                  </TableCell>

                  <TableCell
                    key={"organization:" + program.programId}
                    align="left"
                  >
                    {isBlank(libraries) ? (
                      <Skeleton variant="text" />
                    ) : (
                      notBlank(program.organizationId) &&
                      findOrganization(program.organizationId)
                    )}
                  </TableCell>

                  <TableCell
                    key={"actions-edit:" + program.programId}
                    align="center"
                    style={{ width: 50 }}
                  >
                    <Tooltip title="Edit">
                      <Link
                        to={{
                          pathname: `${url}/${program.programId}`,
                        }}
                        state={{
                          label: "Back to All Programs",
                          previousLocation: "/manage-csp/programs",
                        }}
                      >
                        <EditIcon fontSize="large" color="primary" />
                      </Link>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ),
              programs,
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ProgramTable;
